/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';
import firebase from 'firebase/compat/app';
import { Observable, from } from 'rxjs';
import { groupBy, mergeMap, withLatestFrom } from 'rxjs/operators';

import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { PullColumnList } from 'src/app/common/fancy-firebase-ticket/lists/PullColumns';
import { TicketActionTypes, FancyTicketAction } from 'src/app/app-data/actions/ticket-actions';
import { SupplementalActionTypes } from 'src/app/app-data/actions/supplemental-actions';

import { CalendarWorkdays } from '@touchplan/ticket';
import { RefOrObservable } from '../../fancy-firebase/base/interfaces';

export class TimelineTicketList extends TicketList {
	public projectRef: firebase.database.Reference;

	constructor(
		protected angularLand,
		path: RefOrObservable,
		fancyJoins,
		protected workdays: CalendarWorkdays,
		protected pullColumnList: PullColumnList
	) {
		super(angularLand, path, fancyJoins, workdays);
		this.suppressActionsForLoading = false;
	}

	public $$added(action: AngularFireAction<DatabaseSnapshot<any>>) {
		const ticket = super.$$added(action);
		ticket.isTimelineTicket = true;
		if (this.pullColumnList) {
			ticket.getPullColumnXFromDate = (date: string) => {
				return this.pullColumnList.getXFromDate(date);
			};
			ticket.timelineX = this.pullColumnList.getXFromDate(ticket.view.liveFinish);
		}
		ticket.timelineTicketSplitId = this.splitId(action.key);

		if (this.currentPlanArchive.has(ticket.timelineTicketSplitId[0])) {
			ticket.view.isPlanArchived = this.currentPlanArchive.get(ticket.timelineTicketSplitId[0]);
		}
		return ticket;
	}

	public $$removed(t) {
		// t is undefined, so passing undefined to the parent, which is TicketList.ts,
		// just causing this to break in TicketList.ts instead of here.
		// So going to remove the super.$$removed call, since we have no references to cleanup (Ticket is already undefined)
		// if(!t){ super.$$removed(t); return; }
		if (!t) {
			return;
		}
		//t.timelineTicketSplitId = null;
		t.getPullColumnXFromDate = null;
		t.timelineX = null;
		super.$$removed(t);
	}

	public handleSupplementalEvents(baseObservable: Observable<any>): Observable<FancyTicketAction> {
		//there's only one, so don't bother with the action bs
		return baseObservable.pipe(
			groupBy((action) => (<any>action).type),
			mergeMap((ob$) => {
				switch (ob$.key) {
					case SupplementalActionTypes.pullColumnsUpdated:
						const emits = [];
						this._internalList.forEach((ticket) => {
							const x = this.pullColumnList.getXFromDate(ticket.view.liveFinish);
							if (x !== ticket.timelineX) {
								ticket.timelineX = x;
								emits.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
							}
						});
						return from(emits);
					case SupplementalActionTypes.planArchivedChanged:
						return ob$.pipe(
							withLatestFrom(this.list$),
							mergeMap(([action, tickets]) => {
								const planArchived: Map<string, boolean> = action.payload;

								this.currentPlanArchive = new Map(planArchived);

								const emits = [];
								tickets.forEach((ticket) => {
									if (this.currentPlanArchive.has(ticket.timelineTicketSplitId[0])) {
										ticket.view.isPlanArchived = planArchived.get(ticket.timelineTicketSplitId[0]);
										emits.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
									}
								});
								return from(emits);
							})
						);
					default:
						return from([]);
				}
			})
		);
	}

	public makeCombinedId(ticketId: string, planId: string) {
		return planId + ',' + ticketId;
	}

	protected getTicketRef(ticketId) {
		const splitId = this.splitId(ticketId);
		return this.projectRef.child(splitId[0]).child(splitId[1]);
	}

	public splitId(combinedId: string) {
		return combinedId.split(',') as [string, string];
	}

	public destroy() {
		this.workdays = null;
		this.pullColumnList = null;
		super.destroy();
	}
}
